import { getSeriesWindowing } from "./dicomVoi";
/**
 * Hanging Protocol Service
 *
 * Implements the "Latest Candidate" algorithm for assigning series to viewport slots
 * in a 2x2 grid layout for CT studies.
 *
 * Slot Layout (classification — contrast-based):
 * +------------------+------------------+
 * | Top-Left         | Top-Right        |
 * | Axial Pre-Con    | Axial Post-Con   |
 * +------------------+------------------+
 * | Bottom-Left      | Bottom-Right     |
 * | Coronal Post-Con | Sagittal Post-Con|
 * +------------------+------------------+
 *
 * Slot Layout (segmentation — multi-plane + 3D):
 * +------------------+------------------+
 * | Top-Left         | Top-Right        |
 * | Axial            | Coronal          |
 * +------------------+------------------+
 * | Bottom-Left      | Bottom-Right     |
 * | Sagittal         | 3D View          |
 * +------------------+------------------+
 */

import { SeriesMetadata } from "./dicomMetadataLoader";
import { derivePlane, seriesRegionRank } from "./seriesClassification";
import { logger } from "@/utils/logger";

// =============================================================================
// Types
// =============================================================================

export type SlotId = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export type PlaneType = "Axial" | "Coronal" | "Sagittal";

export interface SlotConfig {
  plane: PlaneType;
  requiresContrast: boolean;
  label: string;
}

export type SeriesAssignment = Record<SlotId, string | null>;

// =============================================================================
// Constants
// =============================================================================

/**
 * Classification layout: contrast-phase based (pre/post for each plane).
 * Used by non-segmentation projects where double-click priority loading
 * targets contrast-enhanced series.
 */
export const SLOT_CONFIGS_CLASSIFICATION: Record<SlotId, SlotConfig> = {
  "top-left": {
    plane: "Axial",
    requiresContrast: false,
    label: "Axial Pre-Contrast",
  },
  "top-right": {
    plane: "Axial",
    requiresContrast: true,
    label: "Axial Post-Contrast",
  },
  "bottom-left": {
    plane: "Coronal",
    requiresContrast: true,
    label: "Coronal Post-Contrast",
  },
  "bottom-right": {
    plane: "Sagittal",
    requiresContrast: true,
    label: "Sagittal Post-Contrast",
  },
};

/**
 * Segmentation layout: one plane per viewport + 3D viewer.
 * Used by segmentation projects for multi-plane nnInteractive dispatch.
 */
export const SLOT_CONFIGS_SEGMENTATION: Record<SlotId, SlotConfig> = {
  "top-left": {
    plane: "Axial",
    requiresContrast: false,
    label: "Axial",
  },
  "top-right": {
    plane: "Coronal",
    requiresContrast: false,
    label: "Coronal",
  },
  "bottom-left": {
    plane: "Sagittal",
    requiresContrast: false,
    label: "Sagittal",
  },
  "bottom-right": {
    plane: "Axial",
    requiresContrast: false,
    label: "3D View",
  },
};

/**
 * Default slot config — classification layout (backwards compatible).
 */
export const SLOT_CONFIGS: Record<SlotId, SlotConfig> = SLOT_CONFIGS_CLASSIFICATION;

/** Get the slot config for a given project mode. */
export function getSlotConfigs(isSegmentation: boolean): Record<SlotId, SlotConfig> {
  return isSegmentation ? SLOT_CONFIGS_SEGMENTATION : SLOT_CONFIGS_CLASSIFICATION;
}

/**
 * Every slot configured for a given plane, in layout order (top-left,
 * top-right, bottom-left, bottom-right). Reads SLOT_CONFIGS instead of
 * hard-coding slot pairs so a layout change moves reformats with it. Empty
 * array when the layout has no slot for the plane.
 */
export function slotsForPlane(
  plane: PlaneType,
  configs: Record<SlotId, SlotConfig> = SLOT_CONFIGS_CLASSIFICATION,
): SlotId[] {
  return SLOT_IDS.filter((slot) => configs[slot].plane === plane);
}

/**
 * The default primary slot (used as the main view when no slot is maximized)
 */
export const DEFAULT_SLOT: SlotId = "top-left";

/**
 * All slot IDs in order (for iteration)
 */
export const SLOT_IDS: SlotId[] = ["top-left", "top-right", "bottom-left", "bottom-right"];

/**
 * Build the cornerstone viewport-id prefix for a hanging-protocol slot.
 * Cornerstone may suffix `_<mountId>` per remount, so callers comparing
 * IDs should use `.startsWith(prefix)` rather than `===`.
 */
export const hangingProtocolViewportPrefix = (slot: SlotId): string => `hangingProtocol_${slot}`;

/**
 * Keywords that indicate contrast-enhanced series
 */
const CONTRAST_KEYWORDS = [
  "contrast",
  "post",
  "portal",
  "arterial",
  "venous",
  "delayed",
  "enhanced",
  "c+",
  "with contrast",
];

/**
 * Keywords that indicate non-contrast series
 */
const NON_CONTRAST_KEYWORDS = [
  "non-con",
  "noncon",
  "pre-con",
  "precon",
  "without",
  "w/o",
  "native",
  "unenhanced",
];

/**
 * Body-part / description tokens that PLAUSIBLY contain the kidneys. If the
 * combined `body_part + series_description` string mentions any of these, the
 * series is kept regardless of any non-kidney tokens it ALSO mentions. Critical
 * for compound body-part strings — `Chest/Abdomen/Pelvis` body CTs are the
 * common kidney-imaging path; they'd be false-positive-dropped by a naive
 * "contains chest" denylist.
 */
const KIDNEY_REGION_KEYWORDS = [
  "kidney",
  "renal",
  "abdomen",
  "abdominal",
  "abdomino",
  "pelvis",
  "pelvic",
  "retroperitoneum",
  "retroperitoneal",
  "urinary",
  "ureter",
];

/**
 * Body-part / description tokens that mean "this series can't contain the
 * kidneys at all" — only applied when no kidney-region token is also present.
 * Compared case-insensitively against `body_part` (DICOM BodyPartExamined) AND
 * `series_description`. "head" is intentionally absent because it appears
 * inside "headfirst" patient-orientation strings. Tokens like "chest" / "lung"
 * are ALSO absent: a "Chest/Abdomen/Pelvis" CT is the standard kidney-imaging
 * acquisition and must not be dropped. Anything we miss here just falls
 * through to the slot assignment, where the user can still drag it manually.
 */
const NON_KIDNEY_ANATOMY_KEYWORDS = [
  "brain",
  "skull",
  "cranial",
  "carotid",
  "neck",
  "cervical",
  "wrist",
  "hand",
  "shoulder",
  "knee",
  "ankle",
  "foot",
  "extremity",
];

/**
 * Keywords that indicate scout/localizer series (should be excluded from hanging protocol)
 */
const SCOUT_KEYWORDS = [
  "scout",
  "localizer",
  "topogram",
  "surview",
  "scanogram",
  "pilot",
  "survey",
];

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Check if series description indicates contrast enhancement
 */
function descriptionIndicatesContrast(description: string | null): boolean {
  if (!description) return false;
  const lower = description.toLowerCase();
  return CONTRAST_KEYWORDS.some((keyword) => lower.includes(keyword));
}

/**
 * Check if series description indicates non-contrast
 */
function descriptionIndicatesNonContrast(description: string | null): boolean {
  if (!description) return false;
  const lower = description.toLowerCase();
  return NON_CONTRAST_KEYWORDS.some((keyword) => lower.includes(keyword));
}

/**
 * Check if a series is a scout/localizer (should be excluded from hanging protocol)
 * Scouts typically have:
 * - Description containing scout keywords
 * - No plane defined (null)
 * - Very few instances (1-3 images)
 */
export function isScoutSeries(meta: SeriesMetadata): boolean {
  // Check series description for scout keywords
  if (meta.series_description) {
    const lower = meta.series_description.toLowerCase();
    if (SCOUT_KEYWORDS.some((keyword) => lower.includes(keyword))) {
      return true;
    }
  }

  // Slice-count floor — a series with < 3 frames can't be segmented (region-
  // grow / lasso / bump all need a volumetric neighbourhood) and has no
  // diagnostic value in a multi-plane MPR layout. The prior version of this
  // check gated on `plane == null`, which missed 2-frame scouts whose plane
  // had been derived from ImageOrientationPatient (the "Case 5" us3d failure
  // mode: two 2-slice topograms auto-assigned to coronal/sagittal slots).
  // 3 is the floor used by the OHIF-ported `is_reconstructable` predicate.
  if (meta.num_instances < 3) {
    return true;
  }

  return false;
}

/**
 * Heuristic: is this series likely NOT a kidney-containing region?
 * Returns true when `body_part` or `series_description` matches a
 * non-kidney anatomy token. Used to keep e.g. brain/chest/extremity series
 * out of the hanging protocol on us3d-* renal-stone labeling projects, where
 * the annotator only assesses abdominopelvic CT. Opt-in via
 * `assignSeriesToSlots({ filterNonKidneyAnatomy: true })` so it doesn't
 * shadow series on general viewers.
 *
 * Conservative on purpose: a missed non-kidney series shows up in the
 * picker, and the annotator can ignore it. A FALSE positive (kidney series
 * dropped) is invisible until someone audits — far worse. See
 * a2zrai/web-demo-backend#359 for the parallel backend-filter discussion.
 */
export function isLikelyNonKidneyAnatomy(meta: SeriesMetadata): boolean {
  const combined = `${meta.body_part ?? ""} ${meta.series_description ?? ""}`.toLowerCase();
  // Allowlist short-circuits: any kidney-region token wins, even if a
  // non-kidney token (e.g. "chest" in "Chest/Abdomen/Pelvis") is also present.
  // This is the load-bearing branch — without it, every body CT tagged with a
  // compound BodyPartExamined string falls through to the denylist and gets
  // dropped, leaving cases with zero valid auto-assignments.
  if (KIDNEY_REGION_KEYWORDS.some((token) => combined.includes(token))) {
    return false;
  }
  return NON_KIDNEY_ANATOMY_KEYWORDS.some((token) => combined.includes(token));
}

/**
 * Determine if a series has contrast based on metadata and description
 */
function seriesHasContrast(meta: SeriesMetadata): boolean {
  // Primary: use the has_contrast field from backend
  if (meta.has_contrast) return true;

  // Secondary: check contrast_agent field
  if (meta.contrast_agent && meta.contrast_agent.trim() !== "") return true;

  // Tertiary: check series description for contrast keywords
  if (descriptionIndicatesContrast(meta.series_description)) return true;

  return false;
}

/**
 * Determine if a series is explicitly non-contrast
 */
function seriesIsNonContrast(meta: SeriesMetadata): boolean {
  // If has_contrast is explicitly false and no contrast agent
  if (!meta.has_contrast && !meta.contrast_agent) {
    // Check if description explicitly mentions non-contrast
    if (descriptionIndicatesNonContrast(meta.series_description)) return true;

    // If no contrast indicators at all, treat as non-contrast
    if (!descriptionIndicatesContrast(meta.series_description)) return true;
  }

  return false;
}

/**
 * Check if a series matches a slot's requirements
 */
function seriesMatchesSlot(meta: SeriesMetadata, config: SlotConfig): boolean {
  // Check plane match (case-insensitive). Backend sometimes omits `plane`;
  // fall back to the cross-product of ImageOrientationPatient when present.
  const seriesPlane = (meta.plane ?? derivePlane(meta))?.toLowerCase();
  const requiredPlane = config.plane.toLowerCase();

  if (seriesPlane !== requiredPlane) {
    return false;
  }

  // Check contrast match
  if (config.requiresContrast) {
    return seriesHasContrast(meta);
  } else {
    return seriesIsNonContrast(meta);
  }
}

// =============================================================================
// Frame of Reference Helpers
// =============================================================================

/**
 * An "acquisition group" uniquely identifies a single volumetric capture:
 * same FrameOfReferenceUID + same AcquisitionNumber = same scanner pass.
 * Series within one group are guaranteed spatially aligned; series across
 * groups may have patient movement between them.
 */
export type AcquisitionGroup = string; // "{frameUID}|{acqNum}" or "{frameUID}" if acqNum unknown

/**
 * Build the acquisition group key for a series.
 * Returns null if neither frame nor acquisition number is known.
 */
export function getAcquisitionGroup(meta: SeriesMetadata): AcquisitionGroup | null {
  const frame = meta.frame_of_reference_uid;
  if (!frame) return null;
  const acq = meta.acquisition_number;
  return acq != null ? `${frame}|${acq}` : frame;
}

/**
 * Determine the dominant acquisition group across current slot assignments.
 * Returns the group shared by the most assigned series, or null if unknown.
 */
export function getDominantAcquisitionGroup(
  assignments: SeriesAssignment,
  seriesMetadata: Record<string, SeriesMetadata>,
): AcquisitionGroup | null {
  const counts = new Map<AcquisitionGroup, number>();
  for (const seriesUid of Object.values(assignments)) {
    if (!seriesUid) continue;
    const meta = seriesMetadata[seriesUid];
    if (!meta) continue;
    const group = getAcquisitionGroup(meta);
    if (group) counts.set(group, (counts.get(group) ?? 0) + 1);
  }
  let best: AcquisitionGroup | null = null;
  let bestCount = 0;
  counts.forEach((count, group) => {
    if (count > bestCount) {
      best = group;
      bestCount = count;
    }
  });
  return best;
}

/**
 * Check whether a new series belongs to a different acquisition group than
 * the current dominant group. Returns mismatch info or null if compatible.
 */
export function checkAcquisitionGroupMismatch(
  newSeriesUid: string,
  currentDominantGroup: AcquisitionGroup | null,
  seriesMetadata: Record<string, SeriesMetadata>,
): { newGroup: AcquisitionGroup; currentGroup: AcquisitionGroup } | null {
  const meta = seriesMetadata[newSeriesUid];
  if (!meta) return null;
  const newGroup = getAcquisitionGroup(meta);
  // If either side is unknown, no mismatch to warn about
  if (!newGroup || !currentDominantGroup) return null;
  if (newGroup === currentDominantGroup) return null;
  return { newGroup, currentGroup: currentDominantGroup };
}

// =============================================================================
// Main Algorithm
// =============================================================================

/**
 * Options for series slot assignment
 */
export interface AssignSeriesToSlotsOptions {
  /**
   * If true (default), fill empty slots with unassigned series when no
   * matching series are found for a slot's requirements (plane + contrast).
   * This allows users to see available series and manually rearrange via drag-drop.
   */
  fillEmptySlots?: boolean;
  /** Series ID to prioritize for top-left slot (the AI inference series) */
  prioritySeriesId?: string | null;
  /** Use segmentation slot layout (multi-plane + 3D) instead of contrast-based */
  isSegmentation?: boolean;
  /**
   * Opt-in for the renal-stone labeling family (us3d-*): exclude series whose
   * body_part or series_description suggests non-kidney anatomy (brain, chest,
   * extremity, …). Keeps incidental head CTs and similar out of the
   * auto-assigned hanging-protocol slots. See `isLikelyNonKidneyAnatomy` and
   * a2zrai/web-demo-backend#359.
   */
  filterNonKidneyAnatomy?: boolean;
}

/**
 * Assign series to viewport slots using the "Latest Candidate" algorithm.
 *
 * Algorithm:
 * 1. Sort all series by series_number descending (newest first)
 * 2. For each slot, find the first matching series (plane + contrast match)
 * 3. Once a series is assigned to a slot, it cannot be used for other slots
 * 4. If fillEmptySlots is true (default), fill remaining slots with unassigned series
 *
 * This enables "Composite View" where a mix of old and new series are displayed,
 * and ensures users always see available series for manual adjustment.
 *
 * @param seriesMetadata - Map of series UID to metadata
 * @param options - Configuration options
 * @returns Assignment of series UIDs to slots
 */
export function assignSeriesToSlots(
  seriesMetadata: Record<string, SeriesMetadata> | undefined,
  options: AssignSeriesToSlotsOptions = {},
): SeriesAssignment {
  const {
    fillEmptySlots = true,
    prioritySeriesId,
    isSegmentation = false,
    filterNonKidneyAnatomy = false,
  } = options;
  const slotConfigs = getSlotConfigs(isSegmentation);

  const result: SeriesAssignment = {
    "top-left": null,
    "top-right": null,
    "bottom-left": null,
    "bottom-right": null,
  };

  if (!seriesMetadata || Object.keys(seriesMetadata).length === 0) {
    return result;
  }

  // Filter out scouts/localizers, then sort by series_number descending (newest first).
  // Demote head/brain series so they only fill viewports when no body-region series
  // remain — relevant for body-focused projects (e.g. urinary-stone) where studies
  // sometimes also include an incidental head series.
  // Region priority (`seriesRegionRank`): 0 = body (best), 1 = neutral, 2 =
  // head/brain. Body series win every empty slot before anything else; head
  // series only fill if nothing else remains.
  //
  // `filterNonKidneyAnatomy` is the project-aware drop for renal-stone
  // cohorts: a brain or chest series in a us3d study is never a valid
  // auto-assignment target. Logged on filter so a misclassified series
  // surfaces in monitoring before someone audits annotations.
  const rawSeries = Object.entries(seriesMetadata)
    .filter(([seriesId, meta]) => {
      if (isScoutSeries(meta)) {
        logger.debug("[assignSeriesToSlots] dropping scout/short series", {
          seriesId,
          description: meta.series_description,
          numInstances: meta.num_instances,
        });
        return false;
      }
      if (filterNonKidneyAnatomy && isLikelyNonKidneyAnatomy(meta)) {
        logger.warn("[assignSeriesToSlots] dropping non-kidney series for renal-stone project", {
          seriesId,
          bodyPart: meta.body_part,
          description: meta.series_description,
        });
        return false;
      }
      return true;
    })
    .sort(([, a], [, b]) => (b.series_number || 0) - (a.series_number || 0));
  const sortedSeries = [...rawSeries].sort(([, a], [, b]) => {
    const ra = seriesRegionRank(a);
    const rb = seriesRegionRank(b);
    if (ra !== rb) return ra - rb;
    return (b.series_number || 0) - (a.series_number || 0);
  });

  // In segmentation mode, bottom-right is the 3D/stats panel — don't assign a series to it
  const assignableSlots = isSegmentation
    ? SLOT_IDS.filter((id) => id !== "bottom-right")
    : SLOT_IDS;

  // Track which series have been assigned
  const assignedSeries = new Set<string>();

  // Phase 0: If we have a priority series (AI inference), force it into top-left
  if (prioritySeriesId && seriesMetadata[prioritySeriesId]) {
    result[DEFAULT_SLOT] = prioritySeriesId;
    assignedSeries.add(prioritySeriesId);
  }

  // Determine the target acquisition group from the priority/first series.
  // Prefer series from the same (FrameOfReferenceUID, AcquisitionNumber) to
  // ensure spatial alignment across viewports.
  const anchorMeta = prioritySeriesId ? seriesMetadata[prioritySeriesId] : sortedSeries[0]?.[1];
  const targetAcquisitionGroup = anchorMeta ? getAcquisitionGroup(anchorMeta) : null;

  // Sort candidates: body-region first (head/brain demoted), then acquisition-group
  // match, then series_number desc. Body-region priority is the outermost key so a
  // higher-numbered head series never beats a body series for any slot.
  const sortedWithGroupPref = [...sortedSeries].sort(([, a], [, b]) => {
    const ra = seriesRegionRank(a);
    const rb = seriesRegionRank(b);
    if (ra !== rb) return ra - rb;
    if (targetAcquisitionGroup) {
      const aMatch = getAcquisitionGroup(a) === targetAcquisitionGroup ? 0 : 1;
      const bMatch = getAcquisitionGroup(b) === targetAcquisitionGroup ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
    }
    return (b.series_number || 0) - (a.series_number || 0);
  });

  // Phase 1: Assign series to slots based on matching requirements
  for (const slotId of assignableSlots) {
    if (result[slotId] !== null) continue; // Skip already assigned (priority)
    const config = slotConfigs[slotId];

    for (const [seriesUid, meta] of sortedWithGroupPref) {
      // Skip already assigned series
      if (assignedSeries.has(seriesUid)) continue;

      // Check if series matches slot requirements
      if (seriesMatchesSlot(meta, config)) {
        result[slotId] = seriesUid;
        assignedSeries.add(seriesUid);
        break;
      }
    }
  }

  // Phase 2: Fill empty slots (relaxed requirements for single-phase studies)
  if (fillEmptySlots) {
    // Pass A: try unassigned plane-matched series (ignore contrast), prefer same acquisition
    for (const slotId of assignableSlots) {
      if (result[slotId] !== null) continue;
      const config = slotConfigs[slotId];
      for (const [seriesUid, meta] of sortedWithGroupPref) {
        if (assignedSeries.has(seriesUid)) continue;
        const plane = (meta.plane ?? derivePlane(meta))?.toLowerCase();
        if (plane === config.plane.toLowerCase()) {
          result[slotId] = seriesUid;
          assignedSeries.add(seriesUid);
          break;
        }
      }
    }

    // Pass B: try any unassigned series, prefer same acquisition.
    // Skipped in segmentation mode where each slot represents a specific
    // plane (axial / coronal / sagittal) — filling a coronal slot with an
    // axial series produces a duplicate-plane view that's worse than an
    // empty slot.
    if (!isSegmentation) {
      for (const slotId of assignableSlots) {
        if (result[slotId] !== null) continue;
        for (const [seriesUid] of sortedWithGroupPref) {
          if (assignedSeries.has(seriesUid)) continue;
          result[slotId] = seriesUid;
          assignedSeries.add(seriesUid);
          break;
        }
      }
    }

    // Pass C removed: never duplicate a series across multiple viewports.
    // Duplicating causes Cornerstone cache conflicts, segmentation registration
    // issues, and confuses the annotator. Empty slots show a placeholder instead.
  }

  return result;
}

/**
 * Get the default window/level for a slot based on its assigned series
 */
export function getSlotWindowing(
  slotId: SlotId,
  seriesMetadata: Record<string, SeriesMetadata> | undefined,
  assignment: SeriesAssignment,
): { center: number; width: number } {
  const seriesUid = assignment[slotId];

  return getSeriesWindowing(seriesUid ? seriesMetadata?.[seriesUid] : undefined);
}

/**
 * Check if the study has series metadata for hanging protocol
 */
export function hasHangingProtocolSupport(
  seriesMetadata: Record<string, SeriesMetadata> | undefined,
): boolean {
  return !!seriesMetadata && Object.keys(seriesMetadata).length > 0;
}

/**
 * Get statistics about the slot assignment
 */
export function getAssignmentStats(assignment: SeriesAssignment): {
  filledSlots: number;
  emptySlots: SlotId[];
} {
  const emptySlots = SLOT_IDS.filter((id) => assignment[id] === null);
  return {
    filledSlots: 4 - emptySlots.length,
    emptySlots,
  };
}
